import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useState, useEffect, useRef } from 'react'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Feature section components
interface FeatureProps {
  title: string
  description: string
  icon?: string
  faIcon?: string
  iconSize?: string
  iconColor?: string
  titleClass?: string
  descriptionClass?: string
}

function Feature({
  title,
  description,
  icon,
  faIcon,
  iconSize = "text-7xl",
  iconColor = "black",
  titleClass = "text-base sm:text-lg md:text-xl font-bold uppercase pb-2 font-secondary",
  descriptionClass = "text-xs sm:text-sm md:text-base text-text-gray leading-relaxed font-text"
}: FeatureProps) {
  return (
    <div className="flex flex-col">
      <div className="text-center pb-5">
        <div className="mb-3 flex justify-center">
          {faIcon ? (
            <i className={`${faIcon} ${iconSize} ${iconColor}`}></i>
          ) : (
            <img 
              src={icon}
              alt={title}
              width={45}
              height={45}
            />
          )}
        </div>
        <h3 className={titleClass}>{title}</h3>
      </div>
      <p className={descriptionClass}>{description}</p>
    </div>
  )
}

// Blog section components
interface BlogPostProps {
  title: string
  image: string
  slug: string
}

function BlogPost({ title, image, slug }: BlogPostProps) {
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }
  
  return (
    <div className="relative bg-white shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
      <div className="relative h-[220px] overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="object-cover w-full h-full border-8 border-white"
        />
      </div>
      <div className="pt-6 pb-5 px-5">
        <h3 className="text-base md:text-lg font-bold uppercase mb-3 text-text-dark font-secondary">
          {title}
        </h3>
        <a href={`/blog/${slug}`} 
           className="text-beaver-green text-sm font-semibold uppercase hover:underline inline-flex items-center font-accent"
           onClick={(e) => handleNavigation(e, `/blog/${slug}`)}
        >
          Read article: {title}
          <i className="fas fa-arrow-right text-xs ml-2"></i>
        </a>
      </div>
    </div>
  )
}

export default function Home() {
  // Blog post data
  const blogPosts = [
    {
      title: "Calgary Deck Repair vs. Replacement: How to Decide",
      image: "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
      slug: "deck-replacement-vs-repair-calgary"
    },
    {
      title: "HOW MUCH DOES IT COST TO BUILD A DECK IN CALGARY?",
      image: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
      slug: "5"
    },
    {
      title: "DO I NEED A PERMIT TO BUILD A DECK IN CALGARY?",
      image: "/assets/images/Decks/Composite Decks/Timbertech-deck-with-PT-wood-skirting-for-underdeck-storage.jpg",
      slug: "4"
    },
    {
      title: "Types of Decks for Your Home in Calgary: Materials & Layouts Guide",
      image: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
      slug: "types-of-decks-calgary"
    }
  ]

  // Hero slideshow images
  const heroImages = [
    "/assets/images/Railing/Radiance-Rail.jpg",
    "/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
    "/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg",
    "/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg"
  ]
  
  // Calgary Projects slideshow images
  const projectImages = [
    "/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
    "/assets/images/2020/04/deck-kevin-therese-0001211.jpg",
    "/assets/images/Decks/Composite Decks/Timbertech-Legacy-covered-deck-in-Tigerwood-surface-and-Pecan-skirting.jpg",
    "/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg",
    "/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg",
    "/assets/images/Railing/Radiance-Rail.jpg",
    "/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg",
    "/assets/images/Decks/Composite Decks/Timbertech-multi-level-deck-in-Dark-Hickory-trim-and-skirting.jpg",
    "/assets/images/2020/04/MG_0857.jpg",
    "/assets/images/2020/04/Pressure-Treated-deck-with-Flower-box-and-Faux-rock.jpg"
  ]
  
  // State for slideshows
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0)
  const [currentProjectSlide, setCurrentProjectSlide] = useState(0)
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'

  // State for FAQ accordion (0 means the first item is open by default. Set to null if you want all closed initially)
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0)

  // Trustindex ref
  const trustIndexRef = useRef<HTMLDivElement>(null);
  
  // Review widget script loader
  useEffect(() => {
    const container = trustIndexRef.current;
    if (!container) return;

    // Prevent duplication
    if (container.querySelector('script[src*="cloudfront.net/bundle.js"]')) return;

    container.innerHTML = ''; 

    const sc = document.createElement("script");
    sc.setAttribute("defer", "true");
    sc.setAttribute("src", "https://dbwx2z9xa7qt9.cloudfront.net/bundle.js?cachebust=1779987327371");
    sc.setAttribute("theme", "light");
    sc.setAttribute("footer", "false");
    sc.setAttribute("widget-type", "carousel");
    sc.setAttribute("token", "6a1872483c21ca4d97da0486");
    sc.setAttribute("apiurl", "https://server.onlinereviews.tech/api/v0.0.9");
    sc.setAttribute("stats", "true");
    sc.setAttribute("addReview", "true");
    sc.setAttribute("profile-pic", "true");
    sc.setAttribute("review-name", "true");
    sc.setAttribute("positive-stars", "true");
    sc.setAttribute("wl", "true");
    sc.setAttribute("wlndig", "https://go.basaropt.com/busy-beaver-construction");
    sc.setAttribute("lang", "us");
    sc.setAttribute("brandStyle", "%7B%22sidebar_background%22%3A%22%23ffffff%22%2C%22sidebar_text%22%3A%22%2316187a%22%2C%22brand_button_text_color%22%3A%22white%22%2C%22brand_main_color%22%3A%22%230005a8%22%2C%22brand_button_border_radius%22%3A%2220px%22%2C%22brand_sidebar_text_color_opacity%22%3A%22%2316187a1a%22%2C%22brand_button_hover%22%3A%22rgb(0%2C%206%2C%20193)%22%2C%22brand_button_active%22%3A%22rgb(0%2C%204%2C%20143)%22%2C%22brand_main_color_opacity%22%3A%22%230005a81a%22%2C%22brand_font%22%3A%22Raleway%22%2C%22star_color%22%3A%22%23fff305%22%2C%22brand_main_background%22%3A%22%23ffffff%22%2C%22brand_card_background%22%3A%22white%22%2C%22poweredByLink%22%3A%22https%3A%2F%2Fwww.basaropt.com%22%2C%22poweredicon%22%3A%22https%3A%2F%2Frecensioni-io-static-folder.s3.eu-central-1.amazonaws.com%2Fpublic_onlinereviews%2Freviews.basaropt.com%2Ftopbar.png%22%7D");
    
    container.appendChild(sc);

    return () => {
      if (container) container.innerHTML = '';
    }
  }, []);
  
  // Handle project slideshow navigation
  const goToPrevSlide = () => {
    setCurrentProjectSlide((prev) => 
      prev === 0 ? projectImages.length - 1 : prev - 1
    )
  }
  
  const goToNextSlide = () => {
    setCurrentProjectSlide((prev) => 
      (prev + 1) % projectImages.length
    )
  }
  
  // Handle lightbox navigation
  const openLightbox = () => {
    setLightboxSlide(currentProjectSlide)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? projectImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % projectImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen])
  
  // Auto-advance hero slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % heroImages.length)
    }, 5000) // Change slide every 5 seconds
    
    return () => clearInterval(interval)
  }, [heroImages.length])

  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }

  // Handle Accordion Toggle
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  // Contact form state
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  })
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [contactSubmitted, setContactSubmitted] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setContactForm({ ...contactForm, [name]: value })
  }

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setContactSubmitting(true);
    setContactSubmitted(false);
    setContactError(null);
    
    try {
      // Swapped failing Firebase URL for your new Formspree ID
      const response = await fetch('https://formspree.io/f/xrerogwr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setContactSubmitted(true);
        setContactForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: ''
        });
        
        // Redirect to the Thank You page
        window.history.pushState({}, '', '/thank-you?form=contact_form');
        window.dispatchEvent(new Event('popstate'));
        window.scrollTo(0, 0);
      } else {
        setContactError('Failed to send message. Please try again later.');
        toast.error("Failed to send message. Please try again later.");
      }
    } catch (error) {
      setContactError('An error occurred. Please try again later.');
      toast.error("An error occurred. Please try again later.");
    } finally {
      setContactSubmitting(false);
    }
  };

  // FAQ Data Structure for the Accordion UI
  const faqData = [
    {
      question: "What deck building services do you offer in Calgary?",
      answer: (
        <p>We provide complete deck building services in Calgary, including custom deck design, new deck construction, deck replacement, and deck repairs. Our team works with materials like pressure-treated wood, composite decking, and cedar to match your budget and durability needs.</p>
      )
    },
    {
      question: "Why should I choose Busy Beaver Construction as my deck builder in Calgary?",
      answer: (
        <p>As a leading deck builder in Calgary, we bring over 28 years of local experience to every project. We understand exactly how the harsh Calgary climate affects outdoor living spaces, which is why we build everything to exceed local building codes. We hold an A+ rating with the BBB and use our own dedicated, in-house crews—never subcontractors—to ensure uncompromising craftsmanship from demolition to final cleanup.</p>
      )
    },
    {
      question: "What types of decking materials are best for Calgary weather?",
      answer: (
        <p>The best material depends on your budget and maintenance preferences. As expert Calgary deck builders, we specialize in premium composite decking from industry leaders like TimberTech, Trex, and AZEK. Composite boards are highly resistant to Calgary's extreme UV rays and freeze-thaw cycles, requiring almost zero maintenance. We also build stunning traditional decks using natural Western Red Cedar, which is naturally rot-resistant and perfect for a classic aesthetic.</p>
      )
    },
    {
      question: "Do I need a city permit to build a new deck in Calgary?",
      answer: (
        <p>Yes, in most cases, the City of Calgary requires a building permit for decks that are over 0.6 meters (2 feet) above grade or attached to your home. Navigating city bylaws and property lines can be confusing, but as your dedicated deck builders, we handle the entire permitting process for you. We ensure your custom deck design is fully compliant before we pour a single concrete footing. You can learn more about the rules in our article: <a href="/blog/4" onClick={(e) => handleNavigation(e as any, '/blog/4')} className="text-beaver-green font-semibold hover:underline">Do I Need a Permit to Build a Deck in Calgary?</a></p>
      )
    },
    {
      question: "How much does a custom deck cost, and do you offer financing?",
      answer: (
        <p>The cost of your new backyard space depends on the square footage, the elevation, and whether you choose natural wood or premium composite materials. During your free onsite consultation, our estimators provide a transparent, detailed quote. To make your dream yard more accessible, we partner with Financeit to offer flexible financing options, allowing you to build your custom deck for as low as $99 a month. For a detailed breakdown of pricing, check out our guide on <a href="/blog/5" onClick={(e) => handleNavigation(e as any, '/blog/5')} className="text-beaver-green font-semibold hover:underline">how much it costs to build a deck in Calgary</a>.</p>
      )
    },
    {
      question: "How long does the deck installation process take?",
      answer: (
        <p>From the moment you approve the 3D design to the final site cleanup, our goal is to minimize disruption to your home. Because we don't farm work out to random contractors, our in-house installation team stays on your site until the project is completely finished. The exact timeline depends on the size of the deck and current weather conditions, but our efficient project management ensures we stay on schedule and on budget.</p>
      )
    },
    {
      question: "Do you provide a warranty on your deck builds in Calgary?",
      answer: (
        <p>Yes, we provide a comprehensive workmanship warranty on all deck installations. As a certified TimberTech Platinum Builder, our composite deck builds are additionally protected by manufacturer warranties ranging from 25 to 50 years against fading, staining, and weathering. We stand behind our 28 years of construction history.</p>
      )
    },
    {
      question: "When is the best time of year to book a deck contractor in Calgary?",
      answer: (
        <p>The best time to book a deck contractor in Calgary is between January and April. Because the local building season is limited by frost and snow, reputable builders fill their summer schedules quickly. Booking in the winter ensures your city permits and custom designs are finalized before the ground thaws.</p>
      )
    },
    {
      question: "Do you offer custom deck design and 3D planning?",
      answer: (
        <p>Yes, every project includes a complete custom design phase. During your free onsite consultation, our estimators evaluate your property lines, elevation, and structural requirements. We provide detailed design plans to ensure your outdoor living space maximizes functionality and strictly adheres to City of Calgary building codes.</p>
      )
    }
  ];

  // Business Schema JSON-LD Data
  const businessSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HomeAndConstructionBusiness",
        "@id": "https://busybeaverconstruction.com/#business",
        "name": "Busy Beaver Construction Ltd.",
        "url": "https://busybeaverconstruction.com/",
        "logo": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Composite%20Decks/timbertech-composite-stock.jpg",
        "description": "Calgary's longest-running deck & fence builder, 25+ years experience. Free on-site estimates on custom decks, fences & outdoor living spaces. Call today.",
        "telephone": "+1-403-286-5400",
        "email": "sales@busybeaverconstruction.com",
        "foundingDate": "1999",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "62 Silverado Ranch Way SW",
          "addressLocality": "Calgary",
          "addressRegion": "AB",
          "addressCountry": "CA"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "08:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "11:00",
            "closes": "16:00"
          }
        ],
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 50.888848904699884,
          "longitude": -114.0886169
        },
        "areaServed": [
          {
            "@type": "Place",
            "name": "Calgary"
          },
          {
            "@type": "Place",
            "name": "Airdrie"
          },
          {
            "@type": "Place",
            "name": "Okotoks"
          }
        ],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "reviewCount": "75",
          "bestRating": "5",
          "worstRating": "1"
        },
        "knowsAbout": [
          "TimberTech Platinum Pro Builder",
          "Composite Decking",
          "Deck Construction",
          "Fence Installation",
          "Pergola Construction",
          "Gazebo Construction",
          "Outdoor Living Spaces",
          "Railing Installation",
          "Landscaping"
        ],
        "foundingLocation": {
          "@type": "Place",
          "name": "Calgary"
        },
        "sameAs": [
          "https://www.facebook.com/BusyBeaverConstruction/",
          "https://www.instagram.com/busybeaverconstruction/",
          "https://x.com/busybeaverconst",
          "https://www.pinterest.com/busybeaverconstruction/",
          "https://www.bbb.org/ca/ab/calgary/profile/deck-builder/busy-beaver-construction-ltd-0017-38381",
          "https://www.homestars.com/profile/2599454-busy-beaver-construction"
        ]
      }
    ]
  };

  // FAQ Schema JSON-LD Data 
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What deck building services do you offer in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We provide complete deck building services in Calgary, including custom deck design, new deck construction, deck replacement, and deck repairs. Our team works with materials like pressure-treated wood, composite decking, and cedar to match your budget and durability needs."
        }
      },
      {
        "@type": "Question",
        "name": "Why should I choose Busy Beaver Construction as my deck builder in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "As a leading deck builder in Calgary, we bring over 28 years of local experience to every project. We understand exactly how the harsh Calgary climate affects outdoor living spaces, which is why we build everything to exceed local building codes. We hold an A+ rating with the BBB and use our own dedicated, in-house crews—never subcontractors—to ensure uncompromising craftsmanship from demolition to final cleanup."
        }
      },
      {
        "@type": "Question",
        "name": "What types of decking materials are best for Calgary weather?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best material depends on your budget and maintenance preferences. As expert Calgary deck builders, we specialize in premium composite decking from industry leaders like TimberTech, Trex, and AZEK. Composite boards are highly resistant to Calgary's extreme UV rays and freeze-thaw cycles, requiring almost zero maintenance. We also build stunning traditional decks using natural Western Red Cedar, which is naturally rot-resistant and perfect for a classic aesthetic."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need a city permit to build a new deck in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, in most cases, the City of Calgary requires a building permit for decks that are over 0.6 meters (2 feet) above grade or attached to your home. Navigating city bylaws and property lines can be confusing, but as your dedicated deck builders, we handle the entire permitting process for you. We ensure your custom deck design is fully compliant before we pour a single concrete footing. You can learn more about the rules in our article: <a href=\"https://busybeaverconstruction.com/blog/4\">Do I Need a Permit to Build a Deck in Calgary?</a>"
        }
      },
      {
        "@type": "Question",
        "name": "How much does a custom deck cost, and do you offer financing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The cost of your new backyard space depends on the square footage, the elevation, and whether you choose natural wood or premium composite materials. During your free onsite consultation, our estimators provide a transparent, detailed quote. To make your dream yard more accessible, we partner with Financeit to offer flexible financing options, allowing you to build your custom deck for as low as $99 a month. For a detailed breakdown of pricing, check out our guide on <a href=\"https://busybeaverconstruction.com/blog/5\">how much it costs to build a deck in Calgary</a>."
        }
      },
      {
        "@type": "Question",
        "name": "How long does the deck installation process take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "From the moment you approve the 3D design to the final site cleanup, our goal is to minimize disruption to your home. Because we don't farm work out to random contractors, our in-house installation team stays on your site until the project is completely finished. The exact timeline depends on the size of the deck and current weather conditions, but our efficient project management ensures we stay on schedule and on budget."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide a warranty on your deck builds in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide a comprehensive workmanship warranty on all deck installations. As a certified TimberTech Platinum Builder, our composite deck builds are additionally protected by manufacturer warranties ranging from 25 to 50 years against fading, staining, and weathering. We stand behind our 28 years of construction history."
        }
      },
      {
        "@type": "Question",
        "name": "When is the best time of year to book a deck contractor in Calgary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The best time to book a deck contractor in Calgary is between January and April. Because the local building season is limited by frost and snow, reputable builders fill their summer schedules quickly. Booking in the winter ensures your city permits and custom designs are finalized before the ground thaws."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer custom deck design and 3D planning?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, every project includes a complete custom design phase. During your free onsite consultation, our estimators evaluate your property lines, elevation, and structural requirements. We provide detailed design plans to ensure your outdoor living space maximizes functionality and strictly adheres to City of Calgary building codes."
        }
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>Busy Beaver Construction | Calgary's Premier Deck & Fence Builder</title>
        <meta name="description" content="Calgary's longest-running deck & fence builder, 25+ years experience. Free on-site estimates on custom decks, fences & outdoor living spaces. Call today" />
        <meta name="keywords" content="Calgary deck builder, custom deck contractor, fence installation Calgary, TimberTech decks, outdoor living spaces, pergolas, privacy walls, deck financing" />
        <link rel="canonical" href="https://busybeaverconstruction.com" />
        <meta property="og:title" content="Busy Beaver Construction | Calgary's Premier Deck & Fence Builder" />
        <meta property="og:description" content="Calgary's leading custom deck and fence builder with 25+ years of experience. Quality craftsmanship, professional service, and beautiful outdoor living spaces." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Railing/Radiance-Rail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Railing/Radiance-Rail.jpg" as="image" />
      </Helmet>
      
      {/* Inject Business and FAQ Schema LD-JSON OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(businessSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Header />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />

      {/* Hero Section */}
      <section className="relative bg-black">
        <div className="absolute inset-0 z-0">
          {heroImages.map((image, index) => (
            <div 
              key={index}
              className="relative h-full w-full"
              style={{ 
                opacity: currentHeroSlide === index ? 0.7 : 0,
                transition: 'opacity 1s ease-in-out',
                position: 'absolute',
                top: 0,
                left: 0
              }}
            >
              <img
                src={image}
                alt={`Custom deck and outdoor living space by Busy Beaver Construction - Calgary's premier deck builder - Slide ${index + 1}`}
                className="object-cover h-full w-full"
                fetchPriority="high"
            />
          </div>
          ))}
        </div>
        
        <div className="container relative z-10 py-14 md:py-32">
          <div className="max-w-2xl mx-auto text-center p-10 bg-black/50 lg:max-w-[47%]">
            <div className="absolute lg:top-[128px] lg:right-[313px]  md:top-[128px] md:right-[63px] top-[56px] right-[16px] overflow-hidden w-[150px] h-[150px]">
              <div className="absolute top-[30px] right-[-35px] bg-[#f71134] text-white py-2 px-10 font-bold uppercase text-xs transform rotate-45 shadow-md font-accent">
                FREE ESTIMATE
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl  text-header-green mb-4 uppercase font-text">
              Calgary Deck Builders for Custom Decks, Fences & Outdoor Living
            </h1>
            <p className="text-base md:text-lg text-white mb-6 font-text">
              For 28 years, Busy Beaver has been building quality custom decks, fences, and outdoor living spaces in Calgary. As experienced deck contractors, our qualified team will help create an outdoor space designed around your needs.
            </p>

            <div className="mb-4">
              <p className="text-xl font-bold font-text" style={{ color: '#f71134', opacity: 1, textShadow: '0 0 1px rgba(0,0,0,0.3)' }}>WE DO<span className="underline font-bold italic font-text">DAM</span>  GOOD WORK</p>
            </div>
            <div className="flex justify-center">
              <a 
                href="/estimate" 
                className="bg-white text-black px-6 py-2 uppercase text-sm rounded hover:bg-beaver-green hover:brightness-110 transition-all duration-300 inline-block font-accent"
                onClick={(e) => handleNavigation(e, '/estimate')}
              >
                CALCULATE YOUR PROJECT
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="bg-white text-black py-8 md:py-10 border-b-2 border-gray-900">
        <div className="container">
          <div className="flex flex-col lg:flex-row items-center justify-between lg:gap-1">
            {/* TimberTech logo */}
            <div className="w-full lg:w-auto flex justify-center lg:justify-end">
              <img
                src="/assets/images/2020/timbertech.png"
                  alt="TimberTech Platinum Pro Builder - Busy Beaver Construction Calgary"
                width={240}
                className="h-auto  object-contain"
                />
              </div>

            {/* Finance text */}
            <div className="w-full lg:w-auto text-center px-0 mx-0">
              <h3 className="text-4xl md:text-3xl lg:text-[44px] font-bold text-gray-800 lg:whitespace-nowrap py-8 font-secondary">
                Finance your new project <br className="hidden md:block" />
                <span className="lg:block lg:mt-3">for as low as $99 a month</span>
              </h3>
            </div>
            
            {/* Financeit logo and button */}
            <div className="w-full lg:w-auto flex flex-col items-center">
              <img
                src="/assets/images/2020/finance-it.png"
                alt="Financeit - Deck Financing Options from Busy Beaver Construction Calgary"
                width={260}
                className="h-auto object-contain mb-4"
              />
              <a
                href="https://www.financeit.ca/en/direct/payment-plan/YT0zMDg2NzMmbD0mcD1wWFE5Y0s0SklWd21tbjhjbTRheUlnJnM9MCZ2PTE=/apply?slug=mRNk6A"
                className="bg-[#3c4c4c] text-white px-8 py-3 font-semibold uppercase rounded hover:brightness-110 transition-all inline-block text-center font-accent"
                target="_blank"
              >
                GET STARTED
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container">
          <h2 className="lg:text-6xl text-5xl md:text-3xl font-bold mb-8 text-center text-text-dark">
            <span className="font-primary">Why Busy Beaver Construction?</span>
          </h2>
          
          <p className="text-center mx-auto mb-6 lg:text-lg text-base font-text text-black leading-relaxed">
            As a leader in the industry and the longest operating Deck and Fence Company in Calgary, 
            we believe in doing business with honesty, integrity, and professionalism. Our reputation 
            is one of the factors that set us apart as the best deck building contractor in Calgary. 
            We are a team of highly qualified professionals with experience in design and installation 
            of decks, fences, pergolas and outdoor living spaces.
          </p>

          <p className="text-center mx-auto mb-12 lg:text-lg text-base text-black leading-relaxed font-text">
            We place high value on communication with our clients and understanding their needs, as it is the key to creating a functional and beautiful design for our clients to enjoy for years.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 pb-10">
            <Feature 
              title="SERVICE"
              faIcon="fas fa-people-carry"
              iconSize="text-7xl"
              titleClass="text-center mx-auto lg:text-3xl text-xl font-secondary"
              descriptionClass="text-center mx-6 lg:text-lg text-base font-text"
              description="Customer service and communication with our clients throughout the entire building process is a top priority for us. The team at Busy Beaver is committed to making your experience with us enjoyable, and will work to exceed your expectations."
            />
            
            <Feature 
              title="28 years IN BUSINESS"
              faIcon="fas fa-business-time"
              iconSize="text-7xl"
              titleClass="text-center mx-auto lg:text-3xl text-xl font-secondary"
              descriptionClass="text-center mx-auto lg:text-lg text-base font-text"
              description="All of our Busy Beaver team members are highly experienced and take pride in the service they offer. We have provided a professional experience for 28 years helping homeowners build the backyard space of their dreams."
            />
            
            <Feature 
              title="QUALITY"
              faIcon="fas fa-drafting-compass"
              iconSize="text-7xl"
              titleClass="text-center mx-auto lg:text-3xl text-xl font-secondary"
              descriptionClass="text-center mx-auto lg:text-lg text-base font-text"
              description="Quality is Priceless. Hiring the wrong contractor, especially the one who is the cheapest, is an expensive mistake. We take pride in our quality of work and our ability to build your project in a timely manner. The contractors that save you money are the ones who do quality work using top quality materials."
            />
          </div>
          
          <div className="flex justify-center">
          <div className="flex items-center space-x-2 border-t border-border-light pt-6">
            <a
              href="https://www.bbb.org/ca/ab/calgary/profile/deck-builder/busy-beaver-construction-ltd-0017-38381"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View Busy Beaver Construction on BBB"
            >
              <img
                src="/assets/images/2021/02/bbb.png"
                alt="Better Business Bureau - Busy Beaver Construction"
                width={160}
                height={50}
              />
            </a>
          </div>
        </div>
        </div>
        <p className="text-sm lg:text-base pt-2 font-bold text-center uppercase text-text-dark font-secondary">BONDED, LICENSED AND INSURED</p>
      </section>

      

      {/* Call To Action */}
      <section className="py-12 lg:py-36 relative">
        <div className="absolute inset-0">
          <img 
            src="/assets/images/Decks/Composite Decks/Timbertech-Silver-Maple.jpg"
            alt="Custom timber deck"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative container text-center">
          <h2 className="text-5xl lg:text-6xl mb-6 text-white text-shadow font-primary">
            Building the deck of your Dreams
          </h2>
          <a 
            href="/appointment" 
            className="bg-beaver-green text-black px-10 py-4 uppercase text-sm lg:text-lg rounded hover:brightness-110 transition-all duration-300 inline-block font-accent"
            onClick={(e) => handleNavigation(e, '/appointment')}
          >
            CONTACT US
          </a>
        </div>
      </section>

      {/* TimberTech Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container">
          <h2 className="text-3xl md:text-6xl mb-12 text-center text-text-dark font-secondary">
                Busy Beaver Construction is<br />
                a TimberTech Platinum Builder
              </h2>
              
          <div className="flex flex-col md:flex-row items-center justify-center ">
            <div className="mb-10 md:mb-0">
              <img
                src="/assets/images/2020/Busy-Beaver-Platinum-2021.png"
                alt="Busy Beaver Construction - TimberTech Platinum Builder Award - Calgary's Top Deck Builder"
                width={550}
                height={350}
                className="max-w-full h-auto rounded-full"
              />
            </div>
            
            <div className="md:max-w-md lg:max-w-xl">
              <p className="text-base lg:text-lg text-text-gray mb-6 font-text">
                TimberTech Platinum Pro contractors are the leading TimberTech installers in their market.
              </p>
              
              <p className="text-base lg:text-lg text-text-gray font-text">
                Contractors that exceed expectations, provide quality craftsmanship and 
                demonstrate the ability and expertise to build outdoor projects using TimberTech/Azek.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-5xl md:text-6xl  mb-4 font-primary text-text-dark">
            Our Process
          </h2>
          
              <div className="mb-12">
                <h3 className="text-2xl  mb-3 font-secondary">Consultation</h3>
                <p className="text-base md:text-lg text-text-gray leading-relaxed mb-6 font-text">
                  An estimator will meet with you directly onsite to work closely with you and go over your vision for the project. 
                  With our years of experience, we will provide you with ideas, knowledge of various products and materials, and a 
                  design to create your new backyard space. After your onsite consultation you should expect your free quotation 
                  within a couple of days.
                </p>
              </div>
              
              <div className="mb-12">
                <h3 className="text-2xl  mb-3 font-secondary">Design</h3>
                <p className="text-base md:text-lg text-text-gray leading-relaxed mb-6 font-text">
                  After approval of your project, we move onto the drawing board. Our team works closely with you to make sure the 
                  design of the project compliments your needs, functionality, and stays within your budget. Color selection, 
                  railing type, and various design aspects are discussed in detail.
                </p>
              </div>
              
              <div className="mb-12">
                <h3 className="text-2xl  mb-3 font-secondary">Construction</h3>
                <p className="text-base md:text-lg text-text-gray leading-relaxed mb-6 font-text">
                  It's now time to make your backyard come to life. We handle everything from demolition to clean up. Our building 
                  crews are highly experienced, professional, and knowledgeable in the industry. Our team is onsite until the 
                  project is completed, and you are satisfied with the craftsmanship of the finished product.
                </p>
              </div>
              
              <div>
                <p className="text-base font-medium text-text-dark mb-4 font-secondary">Contact Busy Beaver Construction:</p>
                <a 
                  href="/appointment" 
                  className="bg-beaver-green text-black px-6 py-3 uppercase text-base rounded hover:brightness-110 transition-all duration-300 inline-block font-accent"
                  onClick={(e) => handleNavigation(e, '/appointment')}
                >
                  REQUEST A FREE ESTIMATE
                </a>
              </div>
            </div>
            
            <div className="space-y-6 lg:pt-10">
              <div className=" overflow-hidden h-[320px] md:h-[415px]">
                <img 
                  src="/assets/images/Privacy Walls/Cedar-Horiztonal-slat-privacy-wall-with-Cedar-Arbor-top.jpg"
                  alt="Custom cedar horizontal slat privacy wall with cedar arbor top by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className=" overflow-hidden h-[320px] md:h-[415px]">
                <img 
                  src="/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg"
                  alt="TimberTech Tigerwood and Mocha custom deck installation by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calgary Projects Section */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container mb-8">
          <h2 className="text-5xl md:text-6xl text-center font-primary text-text-dark">
            Calgary Projects
          </h2>
        </div>

        {/* Full-width image container with slideshow */}
        <div className="w-full overflow-hidden relative border-t-4 border-b-4 border-beaver-green">
          <div className="relative h-[400px] md:h-[600px] w-full">
            {projectImages.map((image, index) => (
              <div 
                key={index}
                className="absolute inset-0 transition-opacity duration-500 cursor-pointer"
                style={{ opacity: currentProjectSlide === index ? 1 : 0 }}
                onClick={openLightbox}
              >
                <img
                  src={image}
                  alt={`Custom Deck Project ${index + 1}`}
                className="object-cover w-full h-full"
              />
            </div>
            ))}
            
            {/* Navigation buttons */}
            <button 
              onClick={goToPrevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none z-10"
              aria-label="Previous slide"
            >
              <i className="fas fa-chevron-left text-2xl"></i>
            </button>
            
            <button 
              onClick={goToNextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none z-10"
              aria-label="Next slide"
            >
              <i className="fas fa-chevron-right text-2xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Associations and Suppliers Section - now using the component */}
      <AssociationsAndSuppliers />

      {/* Trustindex Reviews Widget */}
      <section className="py-12 bg-white">
        <div className="container">
          <h2 className="text-5xl md:text-6xl mb-10 text-center font-primary text-text-dark">
            Customer Reviews
          </h2>
          <div className="flex justify-center">
            {/* Trustindex widget container - logic prevents duplication and handles positioning */}
            <div id="wid_1779987327371" ref={trustIndexRef} className="w-full max-w-5xl mx-auto min-h-[300px]">
              {/* Review widget content injected here */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="py-16 md:py-20 bg-white border-t border-gray-100">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl mb-12 text-center font-primary text-text-dark">
            Frequently Asked Questions About Calgary Deck Building
          </h2>
          
          <div className="space-y-2 border-t border-gray-200">
            {faqData.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={index} className="border-b border-gray-200">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full py-5 flex justify-between items-center focus:outline-none text-left group"
                  >
                    <h3 className="text-xl md:text-2xl font-bold font-secondary text-text-dark pr-6 group-hover:text-beaver-green transition-colors">
                      {faq.question}
                    </h3>
                    <span className="text-beaver-green text-xl md:text-2xl flex-shrink-0">
                      {isOpen ? <i className="fas fa-minus"></i> : <i className="fas fa-plus"></i>}
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isOpen ? 'max-h-[500px] opacity-100 pb-6' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="text-base md:text-lg text-text-gray leading-relaxed font-text">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="relative py-16 md:py-24">
        {/* Full-width background */}
        <div className="absolute inset-0 z-0 bg-[#f5f5f5]">
          <img 
            src="/assets/images/Decks/Wood Decks/Large-outdoor-seating-area-with-Custom-Cedar-privacy-wall.jpg"
            alt="Outdoor covered living space with privacy wall"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="container relative z-10 lg:mt-12">
          <h2 className="text-5xl md:text-6xl mb-12 text-center font-primary text-white text-shadow">
            Blog Posts
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10 max-w-7xl mx-auto">
            {blogPosts.map((post, i) => (
              <BlogPost 
                key={i}
                title={post.title}
                image={post.image}
                slug={post.slug}
              />
            ))}
          </div>
        </div>
      </section>

 {/* Contact Section */}
<section className="py-12 bg-[#222222] text-white">
  <div className="container">
    <h2 className="text-5xl md:text-6xl mb-8 text-center font-primary text-white">
      Contact Us
    </h2>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
      {/* Column 1: Contact Form */}
      <form className="space-y-3 w-full" onSubmit={handleContactSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <div className="py-2">
              <input 
                type="text" 
                placeholder="First Name"
                name="firstName"
                value={contactForm.firstName}
                onChange={handleContactChange}
                required
                className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
              />
            </div>
            <div className="py-2">
              <input 
                type="email" 
                placeholder="Email"
                name="email"
                value={contactForm.email}
                onChange={handleContactChange}
                required
                className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
              />
            </div>
          </div>

          <div>
            <div className="py-2">
              <input
                type="text"
                placeholder="Last Name"
                name="lastName"
                value={contactForm.lastName}
                onChange={handleContactChange}
                required
                className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
              />
            </div>
            <div className="py-2">
              <input
                type="tel"
                placeholder="Phone Number"
                name="phone"
                value={contactForm.phone}
                onChange={handleContactChange}
                required
                className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
              />
            </div>
          </div>
        </div>
        <div className="py-2">
          <textarea 
            placeholder="Message" 
            rows={4}
            name="message"
            value={contactForm.message}
            onChange={handleContactChange}
            required
            className="w-full px-3 py-2 bg-[#222222] border border-white/40 text-white text-sm font-text"
          ></textarea>
        </div>
      
        <div className="relative pt-2">
          <div className="absolute left-0">
            {contactSubmitting ? (
              <p className="text-beaver-green">Sending your message...</p>
            ) : contactSubmitted ? (
              <p className="text-beaver-green">Message sent successfully!</p>
            ) : contactError ? (
              <p className="text-beaver-green">{contactError}</p>
            ) : null}
          </div>
          <div className="text-right">
            <button 
              type="submit" 
              disabled={contactSubmitting}
              className="bg-beaver-green hover:brightness-110 transition-all duration-300 text-black px-8 py-2 text-lg font-medium uppercase rounded font-accent disabled:opacity-50"
            >
              Send Message
            </button>
          </div>
        </div>
      </form>

      {/* Column 2: NAP & Google Map */}
      <div className="w-full h-full flex flex-col space-y-6">
        <div className="font-text text-gray-300 space-y-2">
          <h3 className="text-2xl text-white font-secondary mb-3">Busy Beaver Construction Ltd.</h3>
          <p className="flex items-center gap-2">
            <i className="fas fa-map-marker-alt text-beaver-green w-5"></i> 
            62 Silverado Ranch Way SW, Calgary, AB
          </p>
          <p className="flex items-center gap-2">
            <i className="fas fa-phone-alt text-beaver-green w-5"></i> 
            <a href="tel:+14032865400" className="hover:text-beaver-green transition-colors"
               onClick={() => {
                 if (typeof window !== 'undefined') {
                   (window as any).dataLayer = (window as any).dataLayer || [];
                   (window as any).dataLayer.push({
                     event: 'phone_click'
                   });
                 }
               }}
            >(403) 286-5400</a>
          </p>
        </div>
        
        <div className="w-full flex-grow min-h-[250px] bg-gray-800 rounded overflow-hidden border border-white/20">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2516.8901203433024!2d-114.08861689999999!3d50.88874399999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x53716ee60e8ebd71%3A0x6da301a0d492fb2b!2sBusy%20Beaver%20Construction%20Ltd.!5e0!3m2!1sen!2sin!4v1782881294696!5m2!1sen!2sin" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen={false} 
            loading="lazy" 
            referrerPolicy="strict-origin-when-cross-origin"
            title="Busy Beaver Construction Service Area Calgary"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={projectImages[lightboxSlide]}
                alt={`Custom Deck Project ${lightboxSlide + 1}`}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  )
}